import { asArray, asBoolean, asInteger, asRecord, asString } from "../document/validate.ts";
import type { ZoneStampDefinition } from "./objects.ts";

/**
 * An object type's zone stamp in the game contract (ADR-052, ADR-096 decision 38): written only when a type declares
 * one, its keys in one order, and read back by path.
 */

/**
 * A zone stamp as plain data, its keys in one order and `activation` written only when true (ADR-096 decision 38).
 *
 * @param stamp The stamp.
 * @returns The plain record.
 */
export function plainZoneStamp(stamp: ZoneStampDefinition): Record<string, unknown> {
  return { zoneType: stamp.zoneType, columns: stamp.columns, rows: stamp.rows, originColumn: stamp.originColumn, originRow: stamp.originRow, runs: stamp.runs.map((run) => [run[0], run[1]]), ...(stamp.activation === true ? { activation: true } : {}) };
}

/**
 * Reads a zone stamp. `activation: false` reads as the key left out. Its mask is checked by `validateObjectType`, and
 * that the game registers its zone type by `registerAdapter`.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The stamp.
 * @throws Error naming the path when it is not a record of a zone type, four integers, runs of integer pairs and an
 *   optional boolean.
 */
export function readZoneStamp(value: unknown, path: string): ZoneStampDefinition {
  const record = asRecord(value, path);
  const runs = asArray(record["runs"], `${path}.runs`).map((item, i): readonly [number, number] => {
    const run = asArray(item, `${path}.runs[${i}]`);
    if (run.length !== 2) {
      throw new Error(`${path}.runs[${i}] must be a first column and a length`);
    }
    return [asInteger(run[0], `${path}.runs[${i}][0]`), asInteger(run[1], `${path}.runs[${i}][1]`)];
  });
  const activation = record["activation"] === undefined ? false : asBoolean(record["activation"], `${path}.activation`);
  return { zoneType: asString(record["zoneType"], `${path}.zoneType`), columns: asInteger(record["columns"], `${path}.columns`), rows: asInteger(record["rows"], `${path}.rows`), originColumn: asInteger(record["originColumn"], `${path}.originColumn`), originRow: asInteger(record["originRow"], `${path}.originRow`), runs, ...(activation ? { activation: true as const } : {}) };
}
